import React, { useCallback, useMemo, useState } from "react";
import { AlertTriangle, Layers, PanelLeftClose, PanelLeftOpen } from "lucide-react";

import { usePreferences } from "../context/PreferencesContext";
import { useAuth } from "../auth/AuthContext";

import KumbhGeoMap from "../geo/components/KumbhGeoMap";
import LayerPanel from "../geo/components/LayerPanel";
import IntelPanel from "../geo/components/IntelPanel";
import GeoSearch from "../geo/components/GeoSearch";
import ScenarioPanel from "../geo/components/ScenarioPanel";
import RiskHorizonPanel from "../geo/components/RiskHorizonPanel";
import CrowdSafetyPanel from "../geo/components/CrowdSafetyPanel";
import ForecastPanel from "../geo/components/ForecastPanel";
import ServiceHazardPanel from "../geo/components/ServiceHazardPanel";
import MobilityPanel from "../geo/components/MobilityPanel";
import AssurancePanel from "../geo/components/AssurancePanel";
import { buildTwin } from "../twin/twin";
import { time, LATENCY_BUDGET_MS } from "../assurance/assurance";
import { correlateHazards } from "../geo/services/multiHazard";
import { assessServiceStress } from "../geo/services/serviceStress";
import IncidentCommandDrawer from "../geo/components/IncidentCommandDrawer";
import SectorTable from "../geo/components/SectorTable";
import TimelineControl, { MODE, WINDOW_MINUTES } from "../geo/components/TimelineControl";

import { LAYERS, defaultLayerState, defaultOverlayState, visibleLayersForRole } from "../geo/config/layers";
import { NASHIK, TRIMBAK, MAP_HOME, THEATRE_VIEW } from "../geo/data/kumbhGeo";
import { buildCrowdPoints, buildZoneStatus, FLOW_VECTORS, INCIDENTS, activeIncidents, BASELINE_DENSITY } from "../geo/data/operational";
import { decisionIntelligence, operationalIntelligence } from "../geo/services/engines";
import { resolveProvider, BASEMAP_OPTIONS } from "../geo/config/tileProviders";
import {
  SCENARIO, simulateRouteClosure, simulateDensitySurge,
  simulateEvacuation, simulateResourcesNear,
} from "../geo/services/simulation";
import { riskHorizon } from "../geo/services/riskEngine";
import { theatreTotals, sectorTable } from "../geo/services/metrics";
import { crowdSafetyPicture } from "../geo/services/crowdSafety";
import { useLiveModel } from "../geo/services/useLiveModel";

/* ──────────────────────────────────────────────────────────────────────────
 * InQAi Geospatial Intelligence — Kumbh Operational Digital Twin.
 *
 * Named for what it is. OpenStreetMap is the base-map infrastructure
 * underneath; the product capability is the intelligence, evidence and
 * orchestration layered on top. The page never calls itself "the OSM map".
 *
 * Layout follows §7: layers left, map centre, intelligence right, search and
 * status on top. Panels collapse so the map can take the full width on a
 * wall display.
 * ────────────────────────────────────────────────────────────────────────── */

export default function GeoIntelligence() {
  const { lang } = usePreferences();
  const { user } = useAuth();

  const [layerState, setLayerState] = useState(defaultLayerState);
  const [overlayState, setOverlayState] = useState(defaultOverlayState);
  const [selected, setSelected] = useState(null);
  const [focusTarget, setFocusTarget] = useState(null);
  const [leftOpen, setLeftOpen] = useState(true);
  const [basemap, setBasemap] = useState("osmRaster");
  const [commandIncident, setCommandIncident] = useState(null);
  /* The live model drives §5 forecasting and §7 ingress/egress. Everything
     else still reads the static picture — swapping the whole platform onto
     live state is the next step, not this one. */
  const [running, setRunning] = useState(true);
  const [speed, setSpeed] = useState(10);
  const live = useLiveModel({ running, speed });

  /* The twin is built once per model tick and shared by every panel that
   * needs it. Rebuilding it per panel would triple the work and — worse —
   * let two panels disagree about the same asset. */
  const twin = useMemo(() => buildTwin(live.state), [live.state]);

  /* §16 wants real latency, so the engines are actually timed here rather
   * than estimated. This is the only performance figure in the platform
   * that is measured. */
  const timings = useMemo(() => [
    time("hazards", () => correlateHazards(live.state, { twin }), LATENCY_BUDGET_MS.hazards),
    time("services", () => assessServiceStress(live.state), LATENCY_BUDGET_MS.hazards),
    time("twin", () => buildTwin(live.state), LATENCY_BUDGET_MS.twin),
  ], [live.state, twin]);

  // §5 digital twin
  const [simulation, setSimulation] = useState(null);
  const [pickMode, setPickMode] = useState(null);
  // §6 time control
  const [mode, setMode] = useState(MODE.CURRENT);
  const [offsetMinutes, setOffsetMinutes] = useState(0);
  const [playing, setPlaying] = useState(false);
  // §10 imported records
  const [imported, setImported] = useState([]);

  const provider = useMemo(() => resolveProvider(), []);

  /* Derived once — these are pure functions of static demo data, so
   * recomputing them on every render would be wasted work (§13). */
  const crowdPoints = useMemo(() => buildCrowdPoints(), []);
  const zoneStatus = useMemo(() => buildZoneStatus(), []);
  const allIncidents = useMemo(() => INCIDENTS, []);

  /* In PAST mode, show only incidents already reported at the scrubbed time.
     This filters records we hold by their own timestamps — it does not
     synthesise history. Every other mode shows the current set. */
  const incidents = useMemo(() => {
    if (mode !== MODE.PAST || offsetMinutes <= 0) return allIncidents;
    const cutoff = Date.now() - offsetMinutes * 60000;
    return allIncidents.filter((i) => new Date(i.reportedAt).getTime() <= cutoff);
  }, [allIncidents, mode, offsetMinutes]);

  const activeList = useMemo(
    () => incidents.filter((i) => i.status !== "Resolved"),
    [incidents]
  );

  const ops = useMemo(() => operationalIntelligence({ zoneStatus }), [zoneStatus]);

  /* Anticipatory layer: lead time, bottlenecks, accumulation, counter-flow.
     Recomputed when incidents change so the timeline scrubber moves it too. */
  const horizon = useMemo(
    () => riskHorizon({ flows: FLOW_VECTORS, incidents }),
    [incidents]
  );

  /* Ranked numbers behind the map. Seeded with the risk rows so the table
     and the risk panel cannot disagree about ordering. */
  /* Density-based crowd safety. Deliberately placed FIRST in the right rail:
     it is the measure that answers "can people still move", which outranks
     every percentage on the page. */
  const safety = useMemo(() => crowdSafetyPicture({ flows: FLOW_VECTORS }), []);
  const totals = useMemo(() => theatreTotals({ incidents }), [incidents]);
  const ranked = useMemo(
    () => sectorTable({ riskRows: horizon.zones, incidents }),
    [horizon, incidents]
  );
  /* Declared after `horizon` so the engines consume the same risk output the
     panels display — one computation, one story. */
  const decisions = useMemo(
    () => decisionIntelligence({ zoneStatus, incidents, horizon }),
    [zoneStatus, incidents, horizon]
  );

  /* ── §5 scenario runner ── */
  const runScenario = useCallback((scenario, opts = {}) => {
    let result = null;
    if (scenario === SCENARIO.DENSITY_SURGE) {
      result = simulateDensitySurge(opts.sectorId, { increasePct: opts.increasePct });
    } else if (scenario === SCENARIO.CLOSE_ROUTE) {
      result = simulateRouteClosure(opts.routeId);
    } else if (scenario === SCENARIO.EVACUATION) {
      result = simulateEvacuation(opts.coord);
    } else if (scenario === SCENARIO.RESOURCES_NEAR) {
      result = simulateResourcesNear(opts.coord, { radiusKm: opts.radiusKm });
    }
    setSimulation(result);
    setPickMode(null);
    if (result?.ok) setMode(MODE.SIMULATION);
    return result;
  }, []);

  /* A point-based scenario waits for a map click, then runs. */
  const handlePickPoint = useCallback((coord) => {
    if (!pickMode) return;
    runScenario(pickMode.scenario, { coord, radiusKm: pickMode.radiusKm });
  }, [pickMode, runScenario]);

  const clearSimulation = useCallback(() => {
    setSimulation(null);
    setPickMode(null);
    setMode(MODE.CURRENT);
  }, []);

  const handleImport = useCallback((res) => {
    setImported((prev) => [...prev, ...res.records]);
  }, []);

  /* Role-gated layer set. UI affordance only — see the note in config/layers. */
  const allowedLayerIds = useMemo(
    () => visibleLayersForRole(user?.level).map((l) => l.id),
    [user]
  );

  const toggleLayer = useCallback((id) => {
    setLayerState((s) => ({ ...s, [id]: !s[id] }));
  }, []);

  const toggleOverlay = useCallback((id) => {
    setOverlayState((s) => ({ ...s, [id]: !s[id] }));
  }, []);

  const toggleGroup = useCallback((groupKey, on) => {
    setLayerState((s) => {
      const next = { ...s };
      LAYERS.filter((l) => l.group === groupKey).forEach((l) => { next[l.id] = on; });
      return next;
    });
  }, []);

  const handleFeatureClick = useCallback((props) => {
    setSelected(props);
  }, []);

  const handleSearchSelect = useCallback((r) => {
    setFocusTarget({ coord: r.coord, zoom: r.zoom });
  }, []);

  return (
    <div className="space-y-4">
      {/* ── §14: the DEMO label has to be unmissable, not a footnote ── */}
      <div className="rounded-xl border-2 border-fuchsia-300 bg-fuchsia-50 px-4 py-3">
        <div className="flex items-start gap-2.5">
          <AlertTriangle size={18} className="text-fuchsia-700 flex-shrink-0 mt-0.5" />
          <div>
            <div className="text-sm font-black text-fuchsia-900">
              {lang === "mr" ? "सिम्युलेटेड डेटा — प्रात्यक्षिक" : "SIMULATED DATA — DEMONSTRATION ONLY"}
            </div>
            <p className="mt-1 text-[12px] leading-relaxed text-fuchsia-900/90">
              {lang === "mr"
                ? "या नकाशावरील गर्दीचे आकडे, घटना, तैनाती आणि क्षमता काल्पनिक आहेत. कोणताही सेन्सर, सीसीटीव्ही किंवा शासकीय फीड जोडलेला नाही. ठिकाणांचे निर्देशांक सार्वजनिक स्रोतांवरून अंदाजे आहेत."
                : "Crowd figures, incidents, deployments and capacities on this map are fabricated for demonstration. No sensor, CCTV or government feed is connected. Place coordinates are approximate, from public sources; sector boundaries and routes are illustrative, not the Simhastha plan."}
            </p>
          </div>
        </div>
      </div>

      {/* ── Top bar: search + status ── */}
      <div className="flex flex-wrap items-center gap-3">
        <GeoSearch lang={lang} onSelect={handleSearchSelect} />

        <div className="flex items-center gap-1.5 ml-auto flex-wrap">
          <QuickView label="Nashik" onClick={() => setFocusTarget({ ...NASHIK, coord: NASHIK.center })} />
          <QuickView label="Trimbakeshwar" onClick={() => setFocusTarget({ ...TRIMBAK, coord: TRIMBAK.center })} />
          <QuickView label={lang === "mr" ? "संपूर्ण" : "Theatre"} onClick={() => setFocusTarget({ ...THEATRE_VIEW, coord: THEATRE_VIEW.center })} />
        </div>
      </div>

      {/* ── Command layout ── */}
      <div className="rounded-2xl border border-slate-200 bg-white overflow-hidden shadow-card">
        <div className="flex items-center justify-between px-3 py-2 border-b border-slate-200 bg-slate-50">
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={() => setLeftOpen((v) => !v)}
              className="p-1 rounded hover:bg-slate-200 text-slate-600"
              aria-label={leftOpen ? "Hide layers" : "Show layers"}
            >
              {leftOpen ? <PanelLeftClose size={15} /> : <PanelLeftOpen size={15} />}
            </button>
            <Layers size={14} className="text-slate-500" />
            <span className="text-[12px] font-black text-slate-700">
              InQAi Geospatial Intelligence
            </span>
            <span className="text-[10px] text-slate-400 hidden sm:inline">
              · Kumbh Operational Digital Twin
            </span>
          </div>
          <div className="flex items-center gap-2">
            <div className="flex items-center rounded-md border border-slate-300 overflow-hidden bg-white">
              {BASEMAP_OPTIONS.filter((b) => b.usable).map((b) => (
                <button
                  key={b.id}
                  type="button"
                  onClick={() => setBasemap(b.id)}
                  title={b.demoOnly ? `${b.licence} — ${b.blockedReason}` : b.licence}
                  className={`px-2 py-1 text-[10px] font-bold transition-colors ${
                    basemap === b.id
                      ? "bg-slate-800 text-white"
                      : "text-slate-600 hover:bg-slate-100"
                  }`}
                >
                  {b.label[lang] ?? b.label.en}
                  {b.demoOnly && <span className="ml-1 opacity-60">·demo</span>}
                </button>
              ))}
            </div>
            <div className="hidden xl:block text-[10px] text-slate-400 truncate">
              {provider.label}
            </div>
          </div>
        </div>

        {/* Desktop: one fixed-height row, panels flank the map.
            Mobile: the three stack, so the row height is released and the map
            gets an explicit height of its own — otherwise the two panels
            squeeze it to nothing inside a fixed box. */}
        <div className="flex flex-col lg:flex-row lg:h-[clamp(520px,72vh,900px)]">
          {/* Each aside owns its own scrolling. The panels inside are plain
              blocks that grow to their content — an earlier version let the
              FIRST panel claim h-full and scroll itself, which pushed every
              panel after it below the container, clipped and unreachable. */}
          {leftOpen && (
            <aside className="lg:w-60 flex-shrink-0 border-b lg:border-b-0 lg:border-r border-slate-200 bg-white max-h-56 lg:max-h-none overflow-y-auto">
              <LayerPanel
                lang={lang}
                layerState={layerState}
                overlayState={overlayState}
                onToggleLayer={toggleLayer}
                onToggleOverlay={toggleOverlay}
                onToggleGroup={toggleGroup}
                allowedLayerIds={allowedLayerIds}
              />
              <ScenarioPanel
                lang={lang}
                onRun={runScenario}
                onClear={clearSimulation}
                result={simulation}
                pickMode={pickMode}
                onPickModeChange={setPickMode}
                onImport={handleImport}
              />
            </aside>
          )}

          <main className="relative h-[420px] lg:h-auto lg:flex-1">
            <KumbhGeoMap
              lang={lang}
              layerState={layerState}
              overlayState={overlayState}
              crowdPoints={crowdPoints}
              incidents={incidents}
              flowVectors={FLOW_VECTORS}
              allowedLayerIds={allowedLayerIds}
              focusTarget={focusTarget}
              onFeatureClick={handleFeatureClick}
              simulation={simulation}
              pickMode={pickMode}
              onPickPoint={handlePickPoint}
              importedRecords={imported}
              chokepoints={horizon.bottlenecks}
              riskZones={horizon.zones}
              decisions={decisions}
              basemap={basemap}
            />
          </main>

          <aside className="lg:w-80 flex-shrink-0 border-t lg:border-t-0 lg:border-l border-slate-200 bg-white max-h-72 lg:max-h-none overflow-y-auto">
            <ForecastPanel
              lang={lang}
              live={live}
              running={running}
              speed={speed}
              onRunningChange={setRunning}
              onSpeedChange={setSpeed}
              onFocus={setFocusTarget}
            />
            <ServiceHazardPanel lang={lang} live={live} twin={twin} onFocus={setFocusTarget} />
            <MobilityPanel lang={lang} live={live} twin={twin} onFocus={setFocusTarget} />
            <CrowdSafetyPanel lang={lang} picture={safety} onFocus={setFocusTarget} />
            <IntelPanel
              lang={lang}
              selected={selected}
              incidents={activeList}
              onCommand={(inc) =>
                setCommandIncident({ ...inc, baselineDensity: BASELINE_DENSITY[inc.sector] })
              }
              decisions={decisions}
              ops={ops}
              onFocus={setFocusTarget}
              onDismissSelection={() => setSelected(null)}
            />
            <RiskHorizonPanel lang={lang} horizon={horizon} onFocus={setFocusTarget} />
            <AssurancePanel
              lang={lang}
              live={live}
              twin={twin}
              accuracy={live.accuracy()}
              timings={timings}
            />
            <SectorTable lang={lang} rows={ranked} totals={totals} onFocus={setFocusTarget} />
          </aside>

          {commandIncident && (
            <IncidentCommandDrawer
              lang={lang}
              incident={commandIncident}
              officer={user}
              onClose={() => setCommandIncident(null)}
            />
          )}
        </div>

        {/* §6 — time control sits under the map, spanning the full width. */}
        <TimelineControl
          lang={lang}
          mode={mode}
          onModeChange={setMode}
          offsetMinutes={offsetMinutes}
          onOffsetChange={setOffsetMinutes}
          playing={playing}
          onPlayingChange={setPlaying}
          visibleIncidentCount={incidents.length}
          totalIncidentCount={allIncidents.length}
          hasSimulation={Boolean(simulation?.ok)}
        />
      </div>
    </div>
  );
}

function QuickView({ label, onClick }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className="rounded-lg border border-slate-300 bg-white px-2.5 py-1.5 text-[11px] font-bold text-slate-600 hover:bg-slate-50 hover:border-slate-400 transition-colors"
    >
      {label}
    </button>
  );
}
